import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import DnaRadar from "../components/DnaRadar";
import { runMatchmaker } from "../lib/api";
import { recordEvent } from "../lib/adminApi";

const QUESTIONS = [
  {
    key: "mood",
    title: "What are you in the mood for?",
    options: ["Clean", "Refreshing", "Floral", "Spicy", "Fruity", "Weird", "Expensive", "No idea"],
  },
  {
    key: "adventure",
    title: "How adventurous tonight?",
    options: ["Keep me safe", "A little different", "Surprise me", "Make it strange"],
  },
  {
    key: "serve",
    title: "How do you want to drink it?",
    options: ["G&T", "Martini", "Negroni", "Highball", "Bartender's Choice"],
  },
  {
    key: "budget",
    title: "Budget for 30ml?",
    options: ["Under 200", "200-300", "300-500", "500+", "Doesn't matter"],
  },
];

export default function Matchmaker() {
  const [step, setStep] = useState(0); // 0..3 = questions, 4 = result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const choose = async (value) => {
    const key = QUESTIONS[step].key;
    const next = { ...answers, [key]: value };
    setAnswers(next);

    if (step === 0) recordEvent("matchmaker_start");

    if (step < QUESTIONS.length - 1) {
      setStep((s) => s + 1);
    } else {
      setLoading(true);
      const res = await runMatchmaker(next);
      setResult(res);
      setLoading(false);
      setStep(4);
      recordEvent("matchmaker_complete", { extra: { ...next, personality: res?.personality } });
    }
  };

  const back = () => setStep((s) => Math.max(0, s - 1));
  const restart = () => { setStep(0); setAnswers({}); setResult(null); };

  // RESULT VIEW
  if (step === 4) {
    return <ResultView result={result} answers={answers} onRestart={restart} loading={loading} />;
  }

  const q = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;

  return (
    <div className="px-5 md:px-12 max-w-5xl mx-auto min-h-[78vh] flex flex-col" data-testid="matchmaker-page">
      {/* Top bar */}
      <div className="py-6 flex items-center justify-between">
        <button
          onClick={back}
          disabled={step === 0}
          className="text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] disabled:opacity-20 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
          data-testid="matchmaker-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <p className="overline text-white/40">
          {String(step + 1).padStart(2, "0")} / {String(QUESTIONS.length).padStart(2, "0")}
        </p>
      </div>

      {/* Progress */}
      <div className="h-px bg-white/8 mb-12 relative">
        <motion.div
          className="h-full bg-[#C17F59]"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        />
      </div>

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.45 }}
          className="flex-1 flex flex-col justify-center py-8"
          data-testid={`question-${step}`}
        >
          <p className="overline text-[#C17F59] mb-4">Question {step + 1}</p>
          <h1 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.02] mb-12 max-w-3xl">
            {q.title}
          </h1>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            {q.options.map((opt, i) => (
              <motion.button
                key={opt}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                whileHover={{ y: -3 }}
                onClick={() => choose(opt)}
                className="group relative bg-[#15171A] border border-white/8 hover:border-[#C17F59] px-5 py-7 text-left transition-colors duration-300"
                data-testid={`option-${q.key}-${opt}`}
              >
                <span className="font-display text-2xl leading-tight block">{opt}</span>
                <ArrowRight
                  size={14}
                  className="absolute bottom-4 right-4 text-white/30 group-hover:text-[#C17F59] group-hover:translate-x-1 transition-all"
                />
              </motion.button>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ResultView({ result, answers, onRestart, loading }) {
  if (loading || !result) {
    return (
      <div className="px-5 md:px-12 max-w-5xl mx-auto min-h-[78vh] flex flex-col items-center justify-center" data-testid="matchmaker-loading">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 rounded-full border border-[#C17F59]/50 border-t-[#C17F59] mb-6"
        />
        <p className="overline text-white/50">Reading your gin DNA...</p>
      </div>
    );
  }

  const picks = [
    { label: "Your Safe Pick", gin: result.safe, reason: result.reasoning.safe },
    { label: "Your Upgrade Pick", gin: result.upgrade, reason: result.reasoning.upgrade },
    { label: "Your Wild Card", gin: result.wildcard, reason: result.reasoning.wildcard },
  ];

  return (
    <div className="px-5 md:px-12 max-w-7xl mx-auto py-10" data-testid="matchmaker-result">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="overline text-[#C17F59] mb-3">Your Gin Personality</p>
        <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.95] mb-3">
          You are a <em className="not-italic text-[#C17F59]">{result.personality}</em>.
        </h1>
        <p className="text-white/65 max-w-2xl">
          Based on your answers, we've pulled three gins from the atlas. One to feel safe with, one to grow into, one to dare you.
        </p>
      </motion.div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
        {picks.map((p, i) => (
          <motion.div
            key={p.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.15, duration: 0.6 }}
            className="bg-[#15171A] border border-white/8 hover:border-[#C17F59]/40 transition-colors"
            data-testid={`pick-${i}`}
          >
            <Link to={`/gin/${p.gin.id}`} className="block">
              <div className="p-6 flex flex-col items-center text-center border-b border-white/5">
                <BottleSilhouette category={p.gin.category} size={100} />
                <p className="overline text-[#C17F59] mt-4">{p.label}</p>
                <h3 className="font-display text-3xl mt-1 leading-tight">{p.gin.name}</h3>
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mt-2">
                  {p.gin.country} · ₹{p.gin.price} · {p.gin.abv}%
                </p>
              </div>
              <div className="p-5">
                <DnaRadar items={[{ name: p.gin.name, dna: p.gin.dna, category: p.gin.category }]} height={200} />
                <p className="text-sm text-white/65 leading-relaxed mt-3 line-clamp-3">{p.gin.hook}</p>
                <p className="text-xs text-white/45 italic mt-3">{p.reason}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        <button onClick={onRestart}
                className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-[#D89A74] transition-colors"
                data-testid="matchmaker-restart">
          Take it again
        </button>
        <Link to="/gin/explore"
              className="inline-flex items-center gap-2 border border-white/15 px-6 py-3 text-xs uppercase tracking-[0.3em] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors"
              data-testid="matchmaker-explore-all">
          Explore all 92 gins
        </Link>
      </div>
    </div>
  );
}
